/**
 * Лента и счётчик непрочитанных для колокольчика трекера.
 *
 * Realtime — по SSE, как в кабинете агента: уведомление о назначенной задаче
 * или упоминании бесполезно, если о нём узнают через минуту поллинга.
 * `EventSource` не умеет слать заголовки, поэтому токен уходит в query — так
 * же устроен и сам эндпоинт.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { readTokens } from '@/features/auth/tokens'

import { mapNotification, notificationsApi } from './api'
import { NOTIFICATION_APP, type Notification } from './types'

/**
 * Пауза перед переоткрытием потока. Свой таймер вместо родного авто-реконнекта
 * `EventSource`: токен зашит в URL на момент коннекта, и переоткрывать надо со
 * свежим.
 */
const RECONNECT_DELAY = 5000

const PAGE_SIZE = 20

export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<Notification[]>([])
  const unreadCount = ref(0)
  const total = ref(0)
  const page = ref(1)
  const isLoading = ref(false)
  const isPanelOpen = ref(false)

  /**
   * Живо ли соединение. Наружу — чтобы экраны, живущие событиями, знали, когда
   * событий не будет, и на это время возвращали свой опрос (DEV-2113).
   */
  const isStreamOnline = ref(false)

  /**
   * Последнее пришедшее по потоку уведомление.
   *
   * Отдельной ссылкой, а не первым элементом ленты: лента меняется и от
   * подгрузки страницы в панели, а подписчикам нужно ровно «прилетело новое».
   */
  const lastIncoming = ref<Notification | null>(null)

  let source: EventSource | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let closedByUs = false

  const hasMore = computed(() => items.value.length < total.value)

  async function fetchUnreadCount() {
    try {
      unreadCount.value = await notificationsApi.unreadCount()
    } catch {
      // Счётчик — украшение: молчим, следующий тик подтянет.
    }
  }

  async function fetchPage(next = 1) {
    if (isLoading.value) return

    isLoading.value = true

    try {
      const result = await notificationsApi.list(next, PAGE_SIZE)

      items.value = next === 1 ? result.notifications : [...items.value, ...result.notifications]
      total.value = result.total
      page.value = result.page
    } catch {
      // Пустая панель честнее сообщения об ошибке, которое всё равно нечем
      // исправить: следующий показ панели перезапросит.
    } finally {
      isLoading.value = false
    }
  }

  async function loadMore() {
    if (!hasMore.value) return

    await fetchPage(page.value + 1)
  }

  async function markRead(id: string) {
    const item = items.value.find((n) => n.id === id)
    if (!item || item.isRead) return

    try {
      await notificationsApi.markRead(id)
      item.isRead = true
      item.readAt = new Date().toISOString()
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    } catch {
      // Оставляем непрочитанным: соврать «прочитано» хуже, чем не отметить.
    }
  }

  async function markAllRead() {
    try {
      await notificationsApi.markAllRead()

      items.value.forEach((n) => {
        if (!n.isRead) {
          n.isRead = true
          n.readAt = new Date().toISOString()
        }
      })

      unreadCount.value = 0
    } catch {
      // см. markRead
    }
  }

  function togglePanel() {
    isPanelOpen.value = !isPanelOpen.value

    if (isPanelOpen.value) {
      void fetchPage(1)
    }
  }

  function closePanel() {
    isPanelOpen.value = false
  }

  /** Пришло по SSE. Дедуп по id: реконнект может повторить событие. */
  function onIncoming(n: Notification) {
    if (items.value.some((x) => x.id === n.id)) return

    items.value.unshift(n)
    total.value += 1
    lastIncoming.value = n

    if (!n.isRead) unreadCount.value += 1
  }

  function openStream() {
    if (source) return

    const { accessToken } = readTokens()
    if (!accessToken) return

    const base = import.meta.env.VITE_API_BASE_URL || ''

    // app в query обязателен: ключ подписки на бэкенде составной, и без него
    // в эту вкладку прилетали бы уведомления CRM и кабинета агента.
    source = new EventSource(
      `${base}/api/v1/notifications/stream` +
        `?token=${encodeURIComponent(accessToken)}&app=${encodeURIComponent(NOTIFICATION_APP)}`,
    )

    source.addEventListener('notification', (event) => {
      try {
        onIncoming(mapNotification(JSON.parse((event as MessageEvent).data)))
      } catch {
        // Битый payload не должен рвать поток.
      }
    })

    source.onopen = () => {
      isStreamOnline.value = true

      // Пока висели в оффлайне, могли пропустить события — сверяем счётчик.
      void fetchUnreadCount()
    }

    source.onerror = () => {
      closeSource()

      if (!closedByUs) scheduleReconnect()
    }
  }

  function scheduleReconnect() {
    if (reconnectTimer) return

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null

      // Счётчик тянем перед переоткрытием намеренно: если поток оборвался
      // из-за протухшего токена, обновит его только обычный запрос через
      // интерцептор — EventSource этого не умеет и долбился бы вечно.
      void fetchUnreadCount()
      openStream()
    }, RECONNECT_DELAY)
  }

  function closeSource() {
    isStreamOnline.value = false

    if (!source) return

    source.close()
    source = null
  }

  function connect() {
    closedByUs = false
    void fetchUnreadCount()
    openStream()
  }

  function disconnect() {
    closedByUs = true

    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }

    closeSource()
  }

  return {
    items,
    unreadCount,
    total,
    isLoading,
    isPanelOpen,
    isStreamOnline,
    lastIncoming,
    hasMore,
    fetchUnreadCount,
    fetchPage,
    loadMore,
    markRead,
    markAllRead,
    togglePanel,
    closePanel,
    connect,
    disconnect,
  }
})
